import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { Link, useLocation } from 'react-router-dom'
import { useWindowSize } from '~/common/hooks/useWindowSize'
import { useMutation, useQuery } from '@apollo/client'
import { AddEventViewDocument, GetEventDocument } from '~/api/generated/graphql'
import CommunityEventRow from '~/pages/community/events/CommunityEventRow'
import { useAuth } from '~/auth/Auth'
import { getSafeLocationState, getStartOfCurrentDay } from '~/utils'
import { useCommunity } from '~/contexts/CommunityContext'
import { usePermissions } from '~/pages/posts/PostUtils'
import ToastComponent from '~/common/ToastComponent'
import { EventTabs } from '~/pages/community/events/CommunityEvents'
import LiveStreamQA from '~/pages/community/events/LiveStreamQA'

type CommunityPostState = {
  fromReleaseName?: string
  fromReleaseId?: boolean
}

export const CommunityEvent = () => {
  const { eventId } = useParams<{ communityId: string; eventId: string }>()
  const { state } = useLocation()
  const { isCondensed } = useWindowSize()
  const { hasLeaderPermissions } = usePermissions()
  const { isVeevan, profileVisible, actingSummitAdmin, authUserId, actingSysAdmin } = useAuth()
  const { communityId, isPrivate, companyId } = useCommunity()
  const [canStartLivestream, setCanStartLivestream] = useState(navigator?.userActivation?.isActive)
  const [addEventViewMutation] = useMutation(AddEventViewDocument)
  const isCompany = Boolean(companyId)
  const canEdit = profileVisible && ((isCompany && isVeevan) || hasLeaderPermissions || actingSummitAdmin)
  const { fromReleaseName, fromReleaseId } = getSafeLocationState<CommunityPostState>(state) ?? {}

  const [toastMessage, setToastMessage] = useState('')
  const [showToast, setShowToast] = useState(false)

  const { data: eventData, loading } = useQuery(GetEventDocument, {
    variables: { id: eventId ?? '' },
  })

  const event = eventData?.event
  const isUpcomingEvent = event?.eventStart >= getStartOfCurrentDay()

  const backToLink = fromReleaseId
    ? `/summits/${fromReleaseId}`
    : `../../events?tab=${isUpcomingEvent ? EventTabs.Upcoming : EventTabs.Past}`

  const setToast = (s: string) => {
    setToastMessage(s)
    setShowToast(s != '')
  }

  const isLivestream = event?.isLivestream
  const navigate = useNavigate()

  const canSeeDraft = event?.createdById === authUserId || actingSummitAdmin || actingSysAdmin

  useEffect(() => {
    void addEventViewMutation({ variables: { eventId: eventId ?? '' } })
  }, [addEventViewMutation, eventId])

  if ((event?.draft && !canSeeDraft) || (!loading && !event)) {
    void navigate('../')
  }

  if (loading) return <div className={'loading'}>Loading...</div>

  return (
    <div className={'events-container'}>
      <div className={`nav-simple-zone${isCondensed ? ' condensed' : ''}`}>
        <Link to={backToLink}>&larr; {fromReleaseId ? `Back to ${fromReleaseName}` : 'Back to Events'}</Link>
      </div>
      {isLivestream ? (
        <>
          <CommunityEventRow
            canEdit={canEdit && isUpcomingEvent}
            eventId={eventId}
            communityId={communityId ?? ''}
            setToast={setToast}
            isVeevan={isVeevan}
            canRepost={isVeevan && !isCompany && !isPrivate && isUpcomingEvent && !event?.draft}
            canDelete={canEdit}
            currentLiveStreamEventId={eventId}
            turnOffLivestream={() => navigate(backToLink)}
            inSingleEventView={true}
            joinLivestream={canStartLivestream ? undefined : () => setCanStartLivestream(true)}
            upcoming={isUpcomingEvent}
          />
          {Boolean(event?.qAndAEnabled) && <LiveStreamQA eventId={eventId ?? ''} />}
        </>
      ) : (
        <CommunityEventRow
          key={eventId}
          canEdit={canEdit && isUpcomingEvent}
          eventId={eventId}
          communityId={communityId ?? ''}
          setToast={setToast}
          isVeevan={isVeevan}
          canRepost={isVeevan && !isCompany && !isPrivate && isUpcomingEvent && !event?.draft}
          canDelete={canEdit}
          inSingleEventView={true}
          upcoming={isUpcomingEvent}
        />
      )}
      <ToastComponent show={showToast} onClose={() => setShowToast(false)}>
        {toastMessage ?? ''}
      </ToastComponent>
    </div>
  )
}
